import React, { useEffect } from 'react';
import { Button, FormControl, FormGroup } from 'react-bootstrap';
import { useForm, useFieldArray } from 'react-hook-form';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import classnames from 'classnames';
import { BLACK_LIST_REGEX } from 'v2/utils/formValidator';
import { Contact, DjangoListResponse, EmailAssociation } from 'v2/models';
import restFactory from 'v2/utils/restFactory';
import ConfirmationModal from 'v2/common/ConfirmationModal/ConfirmationModal';
import styles from 'v2/common/EditEmailAssociationModal/modal.module.scss';
import { useTranslation } from 'react-i18next';
import { TranslationKeys } from 'translations';

interface ModalProps {
  contact: Contact;
  show: boolean;
  onClose: (fields?: EmailAssociation[]) => void;
}

const Modal = ({ show, contact, onClose }: ModalProps) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    trigger,
    watch,
    reset: resetForm,
    formState: { isValid, errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: { email_associations: contact.email_associations || [] },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'email_associations',
  });

  const { mutateAsync: getContactByEmail } = useMutation(
    async (email: string) => {
      return await restFactory.get<DjangoListResponse<Contact>>(
        '/dj/api/v1/contact/',
        { email, contact: contact.id }
      );
    }
  );

  const { isLoading: isContactMutateLoading, mutate: contactMutate } =
    useMutation(
      async (email_associations: EmailAssociation[]) => {
        return await restFactory.patch<Contact>(
          `/dj/api/v1/contact/${contact.id}/`,
          { email_associations }
        );
      },
      {
        onSuccess: () => {
          onClose(controlledFields);
        },
        onError: () => {
          toast.error(
            `Error updating ${t(TranslationKeys.contact_one)} emails.`
          );
        },
      }
    );

  useEffect(() => {
    resetForm({ email_associations: contact.email_associations || [] });
  }, [contact.email_associations]);

  useEffect(() => {
    trigger();
  }, [fields]);

  const makePrimary = (index: number) => {
    const currentPrimary = controlledFields.findIndex(
      (field) => field.is_primary
    );

    if (currentPrimary >= 0) {
      setValue(`email_associations.${currentPrimary}.is_primary`, false);
    }
    setValue(`email_associations.${index}.is_primary`, true);
  };

  const makeSearch = async (value: string) => {
    if (
      getValues(`email_associations`).filter(
        (f: EmailAssociation) => f.email === value
      ).length > 1
    ) {
      return false;
    }
    try {
      const contacts = await getContactByEmail(value);
      return contacts.count === 0;
    } catch (_) {
      return false;
    }
  };

  const closeModal = () => {
    resetForm();
    onClose();
  };

  const onSubmit = () => {
    if (isValid) {
      contactMutate(controlledFields);
    }
  };

  const assocs = watch('email_associations');
  const controlledFields = fields.map((field, index) => ({
    ...field,
    ...assocs[index],
  }));

  return (
    <ConfirmationModal
      show={show}
      title="Edit Email"
      onCancel={closeModal}
      isLoading={isContactMutateLoading}
      onOk={handleSubmit(onSubmit)}
      okBtnDisabled={!isValid}
      okBtnText="Save"
      centered={true}
      animation={true}
      className={styles.viewModals}
    >
      <>
        {controlledFields.map((field, index) => (
          <FormGroup key={field.id}>
            {index === 0 && <h6 className={styles.title}>Primary Email</h6>}
            {index === 1 && (
              <h6 className={classnames(styles.title, styles.additional)}>
                Additional Emails
              </h6>
            )}
            <div
              className={classnames('d-flex', 'flex-row', {
                [styles.additional]: index > 1,
              })}
            >
              <div className="flex-column flex-grow-1">
                <FormControl
                  placeholder="Email Address"
                  isInvalid={
                    errors.email_associations?.[index]?.email?.type === 'exist'
                  }
                  {...register(`email_associations.${index}.email`, {
                    validate: {
                      required: (v) => v.length > 5,
                      pattern: (v) =>
                        (v?.match?.(BLACK_LIST_REGEX.email) || []).length > 0,
                      exist: async (v) => await makeSearch(v),
                    },
                  })}
                  onChange={async (e) => {
                    setValue(
                      `email_associations.${index}.email`,
                      e.target.value,
                      {
                        shouldValidate: true,
                        shouldDirty: true,
                      }
                    );
                  }}
                  data-cy={`contact-details-email-modal:additional-email-input-field-${index}`}
                />
                <FormControl.Feedback type="invalid">
                  This email address already belongs to a{' '}
                  {t(TranslationKeys.contact_one)} in your database.
                </FormControl.Feedback>
              </div>
              {index > 0 && (
                <div className="flex-column">
                  <Button
                    disabled={
                      field.is_primary ||
                      errors?.email_associations?.[index]?.email !== undefined
                    }
                    variant="link"
                    onClick={() => makePrimary(index)}
                    data-cy={`contact-details-email-modal:additional-email-make-primary-button-${index}`}
                  >
                    Make Primary
                  </Button>
                </div>
              )}
              <div className="flex-column">
                <Button
                  disabled={
                    field.is_primary ||
                    errors?.email_associations?.[index]?.email !== undefined
                  }
                  variant="link"
                  onClick={() => {
                    remove(index);
                  }}
                  data-cy="contact-details-email-modal:primary-email-delete-button"
                >
                  Delete
                </Button>
              </div>
            </div>
          </FormGroup>
        ))}
        {fields.length < 5 && (
          <Button
            variant="link"
            onClick={() => {
              append({ email: '', is_primary: false });
            }}
            className={styles.addEmail}
            data-cy="contact-details-email-modal:add-email-button"
          >
            + Add Email
          </Button>
        )}
      </>
    </ConfirmationModal>
  );
};

export default Modal;
